import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * A custom element that manages the sidebar drawer menu with tabs and drill-down navigation.
 *
 * @typedef {object} Refs
 * @property {HTMLDivElement} overlay - The overlay backdrop element.
 * @property {HTMLDivElement} panel - The drawer panel element.
 * @property {HTMLButtonElement} tabShop - The Shop tab button.
 * @property {HTMLButtonElement} tabAbout - The About tab button.
 * @property {HTMLDivElement} shopContent - The Shop tab content container.
 * @property {HTMLDivElement} [aboutContent] - The About tab content container.
 * @property {HTMLDivElement} content - The scrollable content area.
 * @property {HTMLDivElement} shopLevel0 - The Shop tab level 0 container.
 * @property {HTMLDivElement[]} level - Array of level containers.
 * @property {HTMLDivElement} [bannerSource] - Hidden holder of banner groups, distributed into levels on init.
 *
 * @extends {Component<Refs>}
 */
class SidebarDrawer extends Component {
  requiredRefs = ['overlay', 'panel', 'content'];

  /** @type {'shop' | 'about'} */
  #activeTab = 'shop';

  /** @type {string | null} */
  #activeParentHandle = null;

  /** @type {string | undefined} */
  #originalStickyState = undefined;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimeout = null;

  /** @type {MediaQueryList} */
  #desktopMql = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.#desktopMql.addEventListener('change', this.#onBreakpointChange);
    this.#distributeBanners();
  }

  updatedCallback() {
    super.updatedCallback?.();
    // Re-run if a Section Rendering API refresh ever restores the original markup.
    this.#distributeBanners();
  }

  /**
   * Distribute banner groups from the hidden source into their target levels.
   * Each group wrapper carries `data-banner-group`: "__main__" goes to the Shop
   * level 0 main list, any other value goes to the level-1 panel whose
   * `data-parent` matches. Runs once; the source is removed afterwards so a
   * re-connect is a no-op.
   */
  #distributeBanners() {
    const source = this.querySelector('[ref="bannerSource"]');
    if (!source) return;

    const shopLevel0 = this.querySelector('[data-tab="shop"] [data-drawer-level="0"]');
    const groups = source.querySelectorAll('.sidebar-drawer__banners[data-banner-group]');
    groups.forEach((group) => {
      const key = group.getAttribute('data-banner-group');
      const target =
        key === '__main__'
          ? shopLevel0
          : this.querySelector(`.sidebar-drawer__level[data-drawer-level="1"][data-parent="${key}"]`);

      if (target) target.appendChild(group);
      // Unmatched handles stay in the source and are dropped when it is removed.
    });

    source.remove();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#desktopMql.removeEventListener('change', this.#onBreakpointChange);
  }

  /**
   * Auto-close drawer and restore header state when crossing the mobile/desktop breakpoint
   * @param {MediaQueryListEvent} event
   */
  #onBreakpointChange = (event) => {
    if (!this.isOpen) return;

    // Force immediate close without animation when crossing breakpoint
    if (this.#closeTimeout) {
      clearTimeout(this.#closeTimeout);
      this.#closeTimeout = null;
    }

    this.removeAttribute('open');
    document.documentElement.removeAttribute('scroll-lock');
    removeTrapFocus();
    this.#resetNavigation();

    const headerComponent = /** @type {HTMLElement | null} */ (document.querySelector('header-component'));
    if (headerComponent) {
      if (this.#originalStickyState !== undefined) {
        if (this.#originalStickyState) {
          headerComponent.setAttribute('data-sticky-state', this.#originalStickyState);
        } else {
          headerComponent.removeAttribute('data-sticky-state');
        }
        this.#originalStickyState = undefined;
      }
      headerComponent.style.removeProperty('contain');
    }
  };

  /**
   * Close on Escape key
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.close();
    }
  };

  /** @returns {boolean} */
  get isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * Open the sidebar drawer
   */
  open() {
    if (this.isOpen) return;

    // Cancel any pending close cleanup to prevent state conflicts
    if (this.#closeTimeout) {
      clearTimeout(this.#closeTimeout);
      this.#closeTimeout = null;
    }

    const headerComponent = /** @type {HTMLElement | null} */ (document.querySelector('header-component'));

    // Temporarily remove contain:layout from header-component so position:fixed works relative to viewport
    if (headerComponent) {
      headerComponent.style.setProperty('contain', 'style');
    }

    // On mobile, force header to non-transparent state then measure position
    if (window.innerWidth < 750 && headerComponent) {
      // Force header to non-transparent state (like sticky active)
      this.#originalStickyState = headerComponent.getAttribute('data-sticky-state') ?? undefined;
      headerComponent.setAttribute('data-sticky-state', 'active');

      // Force layout recalc then measure
      requestAnimationFrame(() => {
        const headerBottom = headerComponent.getBoundingClientRect().bottom;
        const mobileTop = `${Math.max(0, headerBottom)}px`;
        if (this.refs.panel) {
          this.refs.panel.style.setProperty('--sidebar-drawer-mobile-top', mobileTop);
        }
        if (this.refs.overlay) {
          this.refs.overlay.style.setProperty('--sidebar-drawer-mobile-top', mobileTop);
        }
      });
    }

    this.setAttribute('open', '');
    document.documentElement.setAttribute('scroll-lock', '');

    requestAnimationFrame(() => {
      trapFocus(this.refs.panel);
    });
  }

  /**
   * Close the sidebar drawer
   */
  close() {
    if (!this.isOpen) return;

    this.removeAttribute('open');
    document.documentElement.removeAttribute('scroll-lock');
    removeTrapFocus();

    const headerComponent = /** @type {HTMLElement | null} */ (document.querySelector('header-component'));

    // Restore everything after panel slide-out animation completes
    this.#closeTimeout = setTimeout(() => {
      this.#closeTimeout = null;
      this.#resetNavigation();

      if (headerComponent) {
        // Restore sticky state after panel is fully hidden
        if (this.#originalStickyState !== undefined) {
          if (this.#originalStickyState) {
            headerComponent.setAttribute('data-sticky-state', this.#originalStickyState);
          } else {
            headerComponent.removeAttribute('data-sticky-state');
          }
          this.#originalStickyState = undefined;
        }

        headerComponent.style.removeProperty('contain');
      }
    }, 360);
  }

  /**
   * Switch between Shop and About tabs
   * @param {'shop' | 'about'} tab - 'shop' or 'about'
   */
  switchTab(tab) {
    if (tab === this.#activeTab) return;

    this.#activeTab = tab;

    // Update tab buttons
    const tabs = this.querySelectorAll('.sidebar-drawer__tab');
    tabs.forEach((tabEl) => {
      tabEl.classList.remove('sidebar-drawer__tab--active');
      tabEl.setAttribute('aria-selected', 'false');
    });

    const activeTabButton = tab === 'shop' ? this.refs.tabShop : this.refs.tabAbout;
    if (activeTabButton) {
      activeTabButton.classList.add('sidebar-drawer__tab--active');
      activeTabButton.setAttribute('aria-selected', 'true');
    }

    // Update tab content
    const contents = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.sidebar-drawer__tab-content'));
    contents.forEach((content) => {
      if (content.dataset.tab === tab) {
        content.hidden = false;
        content.classList.add('sidebar-drawer__tab-content--active');
      } else {
        content.hidden = true;
        content.classList.remove('sidebar-drawer__tab-content--active');
      }
    });

    // Reset to level 0 when switching tabs
    this.#resetNavigation();

    // Scroll to top
    if (this.refs.content) {
      this.refs.content.scrollTop = 0;
    }
  }

  /**
   * Navigate to a sub-level (drill-down)
   * @param {string} handle - The parent menu item handle
   */
  navigateTo(handle) {
    const currentLevel = this.#getCurrentActiveLevel();
    const targetLevel = this.querySelector(
      `.sidebar-drawer__level[data-drawer-level="1"][data-parent="${handle}"]`
    );

    if (!currentLevel || !targetLevel) return;

    this.#activeParentHandle = handle;

    // Hide current level, show target
    currentLevel.classList.remove('sidebar-drawer__level--active');
    targetLevel.classList.add('sidebar-drawer__level--active');

    if (this.refs.content) {
      this.refs.content.scrollTop = 0;
    }
  }

  /**
   * Go back to the previous level
   */
  goBack() {
    const currentLevel = this.#getCurrentActiveLevel();
    const level0 = this.refs.shopLevel0;

    if (!currentLevel || !level0) return;

    // Hide current level, show level 0
    currentLevel.classList.remove('sidebar-drawer__level--active');
    level0.classList.add('sidebar-drawer__level--active');
    this.#activeParentHandle = null;

    if (this.refs.content) {
      this.refs.content.scrollTop = 0;
    }
  }

  /**
   * Get the currently active level element
   * @returns {HTMLElement | null}
   */
  #getCurrentActiveLevel() {
    const activeContent =
      this.#activeTab === 'shop' ? this.refs.shopContent : this.refs.aboutContent;

    if (!activeContent) return null;

    return activeContent.querySelector('.sidebar-drawer__level--active');
  }

  /**
   * Reset navigation to level 0
   */
  #resetNavigation() {
    // Hide all levels
    const allLevels = this.querySelectorAll('.sidebar-drawer__level');
    allLevels.forEach((level) => {
      level.classList.remove('sidebar-drawer__level--active');
    });

    // Show level 0 for the active tab
    const activeContent =
      this.#activeTab === 'shop' ? this.refs.shopContent : this.refs.aboutContent;

    if (activeContent) {
      const level0 = activeContent.querySelector('[data-drawer-level="0"]');
      if (level0) {
        level0.classList.add('sidebar-drawer__level--active');
      }
    }

    this.#activeParentHandle = null;
  }
}

if (!customElements.get('sidebar-drawer')) {
  customElements.define('sidebar-drawer', SidebarDrawer);
}
